import gql from "graphql-tag";
import RankedIssuerFields from "lib/fragments/RankedIssuerFields";
import YearlyBrazilianCompanyFinancialsFields from "lib/fragments/YearlyBrazilianCompanyFinancialsFields";
import YouTubeVideoFields from "lib/fragments/YouTubeVideoFields";
import TrailingReturnsFields from "lib/graphql/fragments/TrailingReturnsFields";
import CoverFields from "lib/graphql/fragments/issuer/CoverFields";

export default gql`
  fragment BrazilianCompanyIssuerTopLevel on BrazilianCompanyIssuer {
    _id
    cvmCode
    cnpj
    name
    listingSegment
    tickerSymbolPrefix

    morningstarFinancialsCurrency

    isInPortfolio
    viewerDidBookmark
    isBrazilianDepositaryReceipt
    tagAlong {
      commonStock
      preferredStock
      unitStock
    }
    standardLogo {
      url
      geometry
      dominantColor
      colorPalette
    }
    niceLogo {
      url
      geometry
      dominantColor
      colorPalette
    }
    securities {
      _id
      name
      type
      tickerSymbol
      sharesOutstanding
      isin
      corporateGovernanceLevel
      hasQuotes
      trailingReturns {
        ...TrailingReturnsFields
      }
    }
    fiscalYearEnd {
      day
      month
    }
    dateOfIncorporation
    businessDescription
    website
    glassdoorUrl
    reclameAquiUrl
    investorRelationsUrl
    riskFactorsUrl
    briefHistoryUrl
    operatingSegmentsUrl
    ownershipStructureUrl
    descriptionOfActivitiesUrl
    lastYearlyManagementReport {
      dateOfReference
      deliveryDate
      pdfURL
    }
    lastQuarterlyManagementReport {
      dateOfReference
      deliveryDate
      pdfURL
    }
    sector
    headquarters {
      street
      city
      neighborhood
      state
      postalCode
      country
    }
    auditor {
      cvmCode
      name
      dateSince
    }
    auditors {
      cvmCode
      name
    }
    bookkeeper
    reclameAquiRatings {
      reclameAquiUrl
      asOfDate
      finalScore
      classification
    }
    totalCompensations {
      fiscalYearStart
      fiscalYearEnd
      boardOfDirectors
      boardOfExecutiveOfficers
      fiscalCouncil
      totalCompensation
    }
    debtObligations {
      fiscalYearStart
      fiscalYearEnd
      lessThanOneYear
      oneToThreeYears
      threeToFiveYears
      moreThanFiveYears
      totalDebt
    }
    positionOfShareholders {
      ...BrazilianCompanyIssuerShareholderFields
      ...BrazilianCompanyIssuerSharesTotalFields
    }
    majorityShareholders {
      ...BrazilianCompanyIssuerShareholderFields
      ...BrazilianCompanyIssuerSharesTotalFields
    }
    officers {
      ...BrazilianCompanyIssuerOfficerFields
    }
    chiefExecutiveOfficer {
      ...BrazilianCompanyIssuerOfficerFields
    }
    equityInterests {
      name
      country
      geographicalCountry {
        name
        isoCode
        flagSvg {
          ovalHref
          squaredHref
        }
      }
      participation
      marketValue
      bookValue
      dateOfMarketValue
      dateOfBookValue
      descriptionOfActivitiesDeveloped
      acquisitionAndMaintenanceReason
    }
    intangibleAssets {
      type
      description
      term
      eventsThatMayCauseLossOfRights
      lossOfRightsConsequences
    }
    familyRelationships {
      name
      position
      issuerSubsidiaryOrController
      relatedPersonName
      relatedPersonPosition
      relatedPersonIssuerSubsidiaryOrController
      relationship
    }
    propertyPlantEquipment {
      description
      country
      geographicalCountry {
        name
        isoCode
        flagSvg {
          ovalHref
          squaredHref
        }
      }
      state
      city
      typeOfProperty
    }
    otherSecuritiesIssued {
      type
      description
      quantity
      issueDate
      maturity
      debtBalanceOutstanding
    }
    foreignMarketSecurities {
      security
      description
      country
      market
      entity
      tradingAdmissionDate
      tradingSegment
      dateOfListing
      tradingVolume
      proportion
      depositoryBank
      custodian
    }
    freeFloat {
      ...BrazilianCompanyIssuerSharesTotalFields
    }
    treasuryShares {
      ...BrazilianCompanyIssuerSharesTotalFields
    }
    sharesOutstanding {
      ...BrazilianCompanyIssuerSharesTotalFields
    }
    capitalStockComposition {
      totalShares
      totalSharesOfCommonStock
      totalSharesOfPreferredStock
    }
    marketCapitalization(tickerSymbol: $tickerSymbol) {
      totalCapitalization
      asOfDate
    }
    employeeNumbers {
      totalOfEmployees
      asOfDate
    }
    foundedYear
    headquarters {
      street
      city
      neighborhood
      state
      postalCode
      country
    }
    finnacialResultsSpotifyWebcastUrl
    youtubeVideos {
      ...YouTubeVideoFields
    }
    lastFinancialHighlights {
      _id
      fiscalPeriodDate
      fiscalPeriodQuarter
      highlights
    }
    mostRecentRelevantMaterialFact {
      sequentialId
      protocol
      category
      type
      kind
      dateOfReference
      deliveryDate
      url
      downloadUrl
    }
    keyCustomers {
      yearOfReference
      description
      reportDate
    }
    financials {
      yearly {
        ...YearlyBrazilianCompanyFinancialsFields
      }
    }
    subsidiaries {
      _id
      name
      sector
      tickerSymbolPrefix
      primaryTickerSymbol
      niceLogo {
        url
        geometry
        dominantColor
        colorPalette
      }
    }
    nextUpcomingCalendarEvent {
      eventDate
      eventType

      ... on EarningsResultsEvent {
        eventFiscalYear
      }
    }
    b3IndexParticipation(marketIndexSymbol: "IBOV", maturity: QUARTER) {
      marketIndex {
        tickerSymbol
        name
        validSince
        validUntil
      }
      stockParticipationPercent
      components {
        tickerSymbol
        theoreticalQuantity
        stockParticipationPercent
      }
    }
    mandatoryMinimumDividend {
      minimumDividend
      commonPreferredDiscrepancy
      frequency
      reportDate
    }
    ...RankedIssuerFields
    covers {
      ...CoverFields
    }
    cvmRegistrationDate
  }

  ${RankedIssuerFields}
  ${YearlyBrazilianCompanyFinancialsFields}
  ${YouTubeVideoFields}
  ${CoverFields}
  ${TrailingReturnsFields}

  fragment BrazilianCompanyIssuerShareholderFields on BrazilianCompanyIssuerShareholderPosition {
    type
    name
    nationality
    isControllingShareholder
    isGovernmentOrGovernmentRelated
  }

  fragment BrazilianCompanyIssuerSharesTotalFields on BrazilianCompanyIssuerSharesTotal {
    totalShares
    totalSharesOfCommonStock
    totalSharesOfPreferredStock
    percentageOfTotalShares
    percentageOfTotalSharesOfCommonStock
    percentageOfTotalSharesOfPreferredStock
  }

  fragment BrazilianCompanyIssuerOfficerFields on BrazilianCompanyIssuerOfficer {
    board
    name
    occupation
    ellectedByControllingShareholder
    electedDate
    effective
    numberOfConsecutiveTerms
    position
    otherPositionOrDuties
    dateOfBirth
    termOfOffice
  }
`;
